import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Shield, Building2, Users, Mail, CreditCard, Trash2, TrendingUp, Plus, AlertTriangle, Download, Check, X, MapPin, Briefcase, Phone, ArrowLeft, FileSignature, Receipt, CalendarRange, QrCode, Activity, Clock, Wallet, FileText, ChevronRight, Power, BarChart3, FileSearch, Sparkles } from "lucide-react";
import { Switch } from "../components/ui/switch";

function Stat({ icon: Icon, label, value, tone = "light" }) {
  const dark = tone === "dark";
  return (
    <div className={`card-smart p-5 ${dark ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : ""}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] uppercase tracking-widest opacity-70">{label}</div>
        <Icon className="w-4 h-4 text-[#3B82F6]"/>
      </div>
      <div className="font-display text-3xl">{value ?? "–"}</div>
    </div>
  );
}

function AnalyticsPanel({ data, loading, days, onDaysChange, onRefresh }) {
  if (loading && !data) {
    return (
      <div className="card-smart p-12 text-center text-sm text-[#555]" data-testid="sa-analytics-loading">
        Caricamento KPI di adozione…
      </div>
    );
  }
  if (!data) {
    return (
      <div className="card-smart p-12 text-center text-sm text-[#555]" data-testid="sa-analytics-empty">
        Nessun dato analytics disponibile.
      </div>
    );
  }
  const cv = data.cv || {};
  const features = data.features || [];
  const trend = data.trend || [];
  const topCv = data.top_cv_companies || [];
  const topMatch = data.top_match_companies || [];
  const maxFeature = Math.max(1, ...features.map((f) => f.total));
  const maxTrend = Math.max(1, ...trend.map((t) => Math.max(t.cv || 0, t.matches || 0)));
  const totalPremium = features.reduce((s, f) => s + (f.total || 0), 0);
  const totalPremiumRecent = features.reduce((s, f) => s + (f.recent || 0), 0);

  return (
    <div className="space-y-6" data-testid="sa-analytics">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[#7C3AED]">
            <BarChart3 className="w-3.5 h-3.5"/> Analytics di adozione
          </div>
          <div className="text-xs text-[#555] mt-1">
            Ultimi {data.window_days || days} giorni · aggiornato {new Date(data.generated_at).toLocaleString("it-IT")}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex bg-white border border-[#1F2540]/15 rounded-full overflow-hidden">
            {[7, 30, 90, 365].map((d) => (
              <button
                key={d}
                data-testid={`sa-analytics-range-${d}`}
                onClick={() => onDaysChange(d)}
                className={`px-3 py-1.5 text-xs font-semibold transition-all ${days === d ? "bg-[#3B82F6] text-white" : "hover:bg-[#F6F6F6]"}`}
              >
                {d === 365 ? "1A" : `${d}G`}
              </button>
            ))}
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onRefresh}
            data-testid="sa-analytics-refresh"
            className="rounded-full h-9 border-[#1F2540]/20"
          >
            Aggiorna
          </Button>
        </div>
      </div>

      {/* Riepilogo CV / Match AI */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Stat icon={FileText} label="CV caricati" value={cv.total} tone="dark"/>
        <Stat icon={TrendingUp} label={`Nuovi ${data.window_days}gg`} value={cv.recent}/>
        <Stat icon={FileSearch} label="Ricerche AI" value={cv.searches_total}/>
        <Stat icon={Sparkles} label="Match Offerte" value={cv.job_matches_total}/>
        <Stat icon={Activity} label="Match totali" value={(cv.searches_total || 0) + (cv.job_matches_total || 0)} tone="dark"/>
      </div>

      {/* Feature Premium — barre orizzontali */}
      <div className="card-smart p-6" data-testid="sa-analytics-premium">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="font-display text-xl">Feature premium più usate</div>
            <div className="text-xs text-[#555] mt-0.5">Totali globali cross-company · {totalPremium.toLocaleString("it-IT")} azioni ({totalPremiumRecent.toLocaleString("it-IT")} ultimi {data.window_days}gg)</div>
          </div>
        </div>
        <div className="space-y-3">
          {features.map((f) => {
            const pct = (f.total / maxFeature) * 100;
            return (
              <div key={f.key} data-testid={`sa-feature-${f.key}`}>
                <div className="flex justify-between items-center text-xs mb-1.5">
                  <span className="font-semibold">{f.label}</span>
                  <span className="text-[#555]">
                    {f.total.toLocaleString("it-IT")}
                    <span className="text-[#7C3AED] ml-2">+{f.recent} ({data.window_days}gg)</span>
                  </span>
                </div>
                <div className="h-2.5 bg-[#F6F6F6] rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-[#3B82F6] to-[#7C3AED] rounded-full transition-all"
                    style={{ width: `${Math.max(2, pct)}%` }}
                  />
                </div>
              </div>
            );
          })}
          {features.length === 0 && (
            <div className="text-sm text-[#999]">Nessun dato disponibile.</div>
          )}
        </div>
      </div>

      {/* Trend giornaliero (sparkline a barre) */}
      <div className="card-smart p-6" data-testid="sa-analytics-trend">
        <div className="flex items-end justify-between mb-4">
          <div>
            <div className="font-display text-xl">Trend giornaliero</div>
            <div className="text-xs text-[#555] mt-0.5">CV caricati vs Match AI generati</div>
          </div>
          <div className="flex gap-3 text-xs">
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#3B82F6]"/>CV</span>
            <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-[#7C3AED]"/>Match</span>
          </div>
        </div>
        <div className="flex items-end gap-[2px] h-32 overflow-x-auto pb-2">
          {trend.map((t) => (
            <div key={t.date} className="flex flex-col items-center gap-[1px] min-w-[8px] flex-1" title={`${t.date} · CV ${t.cv} · Match ${t.matches}`}>
              <div className="w-full flex flex-col justify-end items-center" style={{ height: "100%" }}>
                <div
                  className="w-full bg-[#7C3AED] rounded-t-sm"
                  style={{ height: `${(t.matches / maxTrend) * 50}%`, minHeight: t.matches > 0 ? "2px" : "0" }}
                />
                <div
                  className="w-full bg-[#3B82F6] rounded-b-sm"
                  style={{ height: `${(t.cv / maxTrend) * 50}%`, minHeight: t.cv > 0 ? "2px" : "0" }}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-[#999] mt-2">
          <span>{trend[0]?.date}</span>
          <span>{trend[trend.length - 1]?.date}</span>
        </div>
      </div>

      {/* Top aziende */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-smart p-6" data-testid="sa-top-cv">
          <div className="font-display text-xl mb-4">Top aziende · CV caricati</div>
          <div className="space-y-2">
            {topCv.length === 0 && <div className="text-sm text-[#999]">Nessun CV ancora caricato.</div>}
            {topCv.map((row, i) => (
              <div key={row.company_id} className="flex items-center justify-between py-2 border-b border-[#F0F0F0] last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-[#7C3AED] w-5">#{i + 1}</span>
                  <span className="text-sm font-semibold">{row.company_name}</span>
                </div>
                <span className="text-sm text-[#555]">{row.count}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="card-smart p-6" data-testid="sa-top-match">
          <div className="font-display text-xl mb-4">Top aziende · Match AI</div>
          <div className="space-y-2">
            {topMatch.length === 0 && <div className="text-sm text-[#999]">Nessun match ancora generato.</div>}
            {topMatch.map((row, i) => (
              <div key={row.company_id} className="flex items-center justify-between py-2 border-b border-[#F0F0F0] last:border-0">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-bold text-[#3B82F6] w-5">#{i + 1}</span>
                  <span className="text-sm font-semibold">{row.company_name}</span>
                </div>
                <span className="text-sm text-[#555]">{row.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function SuperAdminPage() {
  const location = useLocation();
  const [tab, setTab] = useState("overview");

  // Sync tab with ?tab= URL param (per deep-linking dal sidebar)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const t = params.get("tab");
    if (t && ["overview","analytics","companies","users","leads","quotes","payments","anomalies"].includes(t)) {
      setTab(t);
      setCompanyDetail(null);
    }
  }, [location.search]);

  const [overview, setOverview] = useState({});
  const [companies, setCompanies] = useState([]);
  const [companyDetail, setCompanyDetail] = useState(null); // null = elenco, oggetto = dashboard
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [users, setUsers] = useState([]);
  const [leads, setLeads] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [payments, setPayments] = useState([]);
  const [anomalies, setAnomalies] = useState([]);
  const [adoption, setAdoption] = useState(null);
  const [adoptionDays, setAdoptionDays] = useState(30);
  const [adoptionLoading, setAdoptionLoading] = useState(false);
  const [anomalyFilter, setAnomalyFilter] = useState({ status: "pending", from_date: "", to_date: "" });
  const [newOpen, setNewOpen] = useState(false);
  const [editingCompanyId, setEditingCompanyId] = useState(null); // null = creating
  const emptyCo = {
    name: "", legal_form: "SRL", vat_number: "", fiscal_code: "",
    email: "", user_email: "", phone: "", pec: "", website: "",
    address: "", city: "", province: "", zip_code: "",
    shipping_address: "", shipping_city: "", shipping_province: "", shipping_zip_code: "",
    sdi_code: "", iban: "", bank_name: "", rea: "", share_capital: "",
    notes: "",
  };
  const [newCo, setNewCo] = useState(emptyCo);

  const loadCompanies = async () => setCompanies((await api.get("/admin/companies")).data);

  const openCompanyDetail = async (cid) => {
    setLoadingDetail(true);
    try {
      const r = await api.get(`/admin/companies/${cid}/dashboard`);
      setCompanyDetail(r.data);
    } catch (e) {
      toast.error("Errore caricamento dashboard azienda");
    } finally { setLoadingDetail(false); }
  };

  const closeCompanyDetail = () => setCompanyDetail(null);

  const toggleCompanyActive = async (cid, currentActive) => {
    const newActive = !currentActive;
    let reason = "";
    if (!newActive) {
      reason = window.prompt("Motivo della disattivazione (visibile internamente):", "");
      if (reason === null) return; // cancel
    } else {
      if (!window.confirm("Vuoi riattivare questa azienda? Gli utenti potranno tornare ad accedere.")) return;
    }
    try {
      await api.patch(`/admin/companies/${cid}/toggle-active?active=${newActive}${reason ? `&reason=${encodeURIComponent(reason)}` : ""}`);
      toast.success(newActive ? "Azienda riattivata" : "Azienda disattivata · accesso bloccato");
      // Refresh local data
      setCompanies((prev) => prev.map((c) => c.id === cid ? { ...c, active: newActive } : c));
      if (companyDetail && companyDetail.company.id === cid) {
        setCompanyDetail({ ...companyDetail, company: { ...companyDetail.company, active: newActive } });
      }
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const deleteCompany = async (cid, name) => {
    const phrase = window.prompt(
      `⚠️ ELIMINAZIONE DEFINITIVA\n\nStai per eliminare "${name}" e TUTTI i dati associati (utenti, dipendenti, presenze, documenti, ferie, rimborsi, QR, rapportini, cedolini, pagamenti).\n\nL'operazione è IRREVERSIBILE.\n\nDigita ELIMINA per confermare:`,
      ""
    );
    if (phrase !== "ELIMINA") {
      if (phrase !== null) toast.error("Conferma annullata · digita esattamente ELIMINA");
      return;
    }
    try {
      const r = await api.delete(`/admin/companies/${cid}`);
      const cnt = Object.values(r.data.deleted_cascade || {}).reduce((a, b) => a + b, 0);
      toast.success(`Azienda eliminata · ${cnt} record rimossi in cascata`);
      setCompanies((prev) => prev.filter((c) => c.id !== cid));
      if (companyDetail && companyDetail.company.id === cid) closeCompanyDetail();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    }
  };

  useEffect(() => {
    api.get("/admin/overview").then((r) => setOverview(r.data)).catch(() => toast.error("Accesso negato"));
  }, []);

  useEffect(() => {
    if (tab === "companies") loadCompanies();
    if (tab === "users") api.get("/admin/users").then((r) => setUsers(r.data));
    if (tab === "leads") api.get("/admin/leads").then((r) => setLeads(r.data));
    if (tab === "quotes") api.get("/admin/enterprise-quotes").then((r) => setQuotes(r.data));
    if (tab === "payments") api.get("/admin/payments").then((r) => setPayments(r.data));
    if (tab === "anomalies") loadAnomalies();
    if (tab === "analytics") loadAdoption(adoptionDays);
  }, [tab]);

  const loadAdoption = async (days) => {
    setAdoptionLoading(true);
    try {
      const r = await api.get(`/admin/analytics/adoption?days=${days}`);
      setAdoption(r.data);
    } catch (e) {
      toast.error("Errore caricamento analytics");
    } finally { setAdoptionLoading(false); }
  };

  const loadAnomalies = async () => {
    try {
      const params = new URLSearchParams();
      if (anomalyFilter.status) params.set("status", anomalyFilter.status);
      if (anomalyFilter.from_date) params.set("from_date", anomalyFilter.from_date);
      if (anomalyFilter.to_date) params.set("to_date", anomalyFilter.to_date);
      const r = await api.get(`/admin/anomalies?${params.toString()}`);
      setAnomalies(r.data);
    } catch { toast.error("Errore caricamento anomalie"); }
  };

  const decideAnomaly = async (id, status) => {
    const note = status === "approved"
      ? prompt("Motivazione approvazione (es. visita cliente autorizzata):") || ""
      : prompt("Motivazione rifiuto:") || "";
    try {
      await api.patch(`/admin/anomalies/${id}`, { status, note });
      toast.success(status === "approved" ? "Anomalia approvata" : "Anomalia rifiutata");
      loadAnomalies();
    } catch { toast.error("Errore"); }
  };

  const exportAnomalies = async () => {
    try {
      const params = new URLSearchParams();
      if (anomalyFilter.from_date) params.set("from_date", anomalyFilter.from_date);
      if (anomalyFilter.to_date) params.set("to_date", anomalyFilter.to_date);
      const r = await api.get(`/admin/anomalies/export.csv?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `anomalie_${new Date().toISOString().slice(0,10)}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Errore export"); }
  };

  const sendWeeklyReportNow = async () => {
    if (!confirm("Inviare ora il report settimanale ai super admin?")) return;
    try {
      const r = await api.post("/admin/weekly-report/run-now");
      toast.success(`Report inviato a ${r.data.sent_to} super admin · ${r.data.pending} anomalie pending`);
    } catch { toast.error("Errore invio report"); }
  };

  const createCompany = async () => {
    if (!newCo.name.trim()) { toast.error("Ragione sociale obbligatoria"); return; }
    try {
      if (editingCompanyId) {
        await api.patch(`/admin/companies/${editingCompanyId}`, newCo);
        toast.success("Azienda aggiornata");
      } else {
        await api.post("/admin/companies", newCo);
        toast.success("Azienda creata");
      }
      setNewCo(emptyCo);
      setEditingCompanyId(null);
      setNewOpen(false);
      loadCompanies();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const openEditCompany = async (cid) => {
    try {
      const r = await api.get(`/admin/companies/${cid}`);
      const c = r.data || {};
      setNewCo({ ...emptyCo, ...c });
      setEditingCompanyId(cid);
      setNewOpen(true);
    } catch { toast.error("Errore caricamento azienda"); }
  };

  const openNewCompany = () => {
    setNewCo(emptyCo);
    setEditingCompanyId(null);
    setNewOpen(true);
  };

  const setPlan = async (uid, plan) => {
    try {
      await api.patch(`/admin/users/${uid}/plan?plan=${plan}`);
      toast.success(`Piano impostato: ${plan}`);
      setUsers((await api.get("/admin/users")).data);
    } catch (e) { toast.error("Errore"); }
  };

  const delUser = async (uid) => {
    if (!window.confirm("Eliminare definitivamente questo utente?")) return;
    await api.delete(`/admin/users/${uid}`);
    toast.success("Eliminato");
    setUsers((await api.get("/admin/users")).data);
  };

  const tabs = [
    { id: "overview", label: "Panoramica", icon: TrendingUp },
    { id: "analytics", label: "Analytics", icon: BarChart3 },
    { id: "companies", label: "Aziende", icon: Building2 },
    { id: "users", label: "Utenti", icon: Users },
    { id: "anomalies", label: "Anomalie", icon: AlertTriangle },
    { id: "leads", label: "Lead", icon: Mail },
    { id: "quotes", label: "Preventivi Enterprise", icon: Briefcase },
    { id: "payments", label: "Pagamenti", icon: CreditCard },
  ];

  return (
    <div className="p-4 md:p-12" data-testid="superadmin-page">
      <div className="mb-8">
        <div className="inline-flex items-center gap-2 bg-[#3B82F6]/10 text-[#7C3AED] text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 rounded-full mb-3">
          <Shield className="w-3.5 h-3.5"/> Super Admin
        </div>
        <h1 className="font-display text-4xl">Console Platform</h1>
        <p className="text-[#555] mt-1 text-sm">Controllo cross-company · visibilità totale · gestione piani</p>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {tabs.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`sa-tab-${t.id}`}
              className={`rounded-full px-5 py-2.5 text-sm font-semibold flex items-center gap-2 transition-all ${active ? "bg-[#3B82F6] text-white shadow-lg shadow-[#3B82F6]/30" : "bg-white border border-[#1F2540] hover:border-[#3B82F6]"}`}
            >
              <Icon className="w-4 h-4"/> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="sa-overview">
          <Stat icon={Building2} label="Aziende" value={overview.companies} tone="dark"/>
          <Stat icon={Users} label="Utenti totali" value={overview.users}/>
          <Stat icon={Users} label="Dipendenti" value={overview.employees}/>
          <Stat icon={TrendingUp} label="Timbrature" value={overview.attendances}/>
          <Stat icon={Mail} label="Lead totali" value={overview.leads}/>
          <Stat icon={CreditCard} label="Transazioni" value={overview.payments_total}/>
          <Stat icon={CreditCard} label="Pagati" value={overview.payments_paid} tone="dark"/>
          <Stat icon={TrendingUp} label="Eventi" value={overview.events}/>
          <Stat icon={TrendingUp} label="Documenti" value={overview.documents}/>
          <Stat icon={TrendingUp} label="Ticket aperti" value={overview.tickets_open}/>
        </div>
      )}

      {tab === "analytics" && (
        <AnalyticsPanel
          data={adoption}
          loading={adoptionLoading}
          days={adoptionDays}
          onDaysChange={(d) => { setAdoptionDays(d); loadAdoption(d); }}
          onRefresh={() => loadAdoption(adoptionDays)}
        />
      )}

      {tab === "companies" && !companyDetail && (
        <div data-testid="sa-companies">
          <div className="flex justify-end mb-4">
            <Dialog open={newOpen} onOpenChange={setNewOpen}>
              <DialogTrigger asChild>
                <Button data-testid="new-company-btn" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED] h-10 btn-glow">
                  <Plus className="w-4 h-4 mr-2"/> Nuova azienda
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-3xl border-0 max-w-xl">
                <DialogHeader><DialogTitle className="font-display text-2xl">Nuova anagrafica azienda</DialogTitle></DialogHeader>
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2"><Label>Ragione sociale *</Label><Input data-testid="newco-name" required className="rounded-xl mt-1" value={newCo.name} onChange={(e)=>setNewCo({...newCo, name: e.target.value})}/></div>
                  <div>
                    <Label>Forma giuridica</Label>
                    <Select value={newCo.legal_form || undefined} onValueChange={(v)=>setNewCo({...newCo, legal_form: v})}>
                      <SelectTrigger className="rounded-xl mt-1"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        {["SRL","SRLS","SPA","SNC","SAS","Ditta Individuale","Cooperativa","Altro"].map((f)=>(<SelectItem key={f} value={f}>{f}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div><Label>P.IVA</Label><Input className="rounded-xl mt-1" value={newCo.vat_number} onChange={(e)=>setNewCo({...newCo, vat_number: e.target.value})}/></div>
                  <div><Label>Codice Fiscale</Label><Input className="rounded-xl mt-1" value={newCo.fiscal_code} onChange={(e)=>setNewCo({...newCo, fiscal_code: e.target.value})}/></div>
                  <div><Label>Email</Label><Input type="email" className="rounded-xl mt-1" value={newCo.email} onChange={(e)=>setNewCo({...newCo, email: e.target.value})}/></div>
                  <div><Label>Telefono</Label><Input className="rounded-xl mt-1" value={newCo.phone} onChange={(e)=>setNewCo({...newCo, phone: e.target.value})}/></div>
                  <div className="col-span-2 grid grid-cols-3 gap-2">
                    <div className="col-span-2"><Label>Città</Label><Input className="rounded-xl mt-1" value={newCo.city} onChange={(e)=>setNewCo({...newCo, city: e.target.value})}/></div>
                    <div><Label>Prov.</Label><Input maxLength={2} className="rounded-xl mt-1 uppercase" value={newCo.province} onChange={(e)=>setNewCo({...newCo, province: e.target.value.toUpperCase()})}/></div>
                  </div>
                </div>
                <DialogFooter><Button onClick={createCompany} data-testid="newco-save" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">Crea azienda</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="card-smart overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-[#1F2540]"><tr className="text-left"><th className="p-4">Azienda</th><th className="p-4">P.IVA</th><th className="p-4">Piano</th><th className="p-4">Utenti</th><th className="p-4">Dipendenti</th><th className="p-4">Creata</th><th className="p-4 text-center">Stato</th><th className="p-4 text-right">Azioni</th></tr></thead>
              <tbody>
                {companies.length === 0 && <tr><td colSpan="8" className="p-8 text-center text-[#757575]">Nessuna azienda. Creane una con "Nuova azienda".</td></tr>}
                {companies.map((c) => (
                  <tr key={c.id} className={`border-b border-[#1F2540] hover:bg-[#0A0E27] cursor-pointer transition-colors ${c.active === false ? "opacity-60 bg-red-50/30" : ""}`} onClick={()=>openCompanyDetail(c.id)} data-testid={`company-row-${c.id}`}>
                    <td className="p-4 font-semibold">{c.name}<div className="text-xs text-[#757575] font-normal">{c.legal_form || "—"}</div></td>
                    <td className="p-4 font-mono text-xs">{c.vat_number || "—"}</td>
                    <td className="p-4"><span className="text-xs bg-[#1E2347] text-[#7C3AED] px-2.5 py-1 rounded-full font-semibold uppercase">{c.plan}</span></td>
                    <td className="p-4">{c.users_count}</td>
                    <td className="p-4">{c.employees_count}</td>
                    <td className="p-4 text-xs text-[#757575]">{new Date(c.created_at).toLocaleDateString("it-IT")}</td>
                    <td className="p-4 text-center" onClick={(e)=>e.stopPropagation()}>
                      <div className="flex items-center justify-center gap-2" data-testid={`company-active-toggle-${c.id}`}>
                        <Switch
                          checked={c.active !== false}
                          onCheckedChange={()=>toggleCompanyActive(c.id, c.active !== false)}
                          data-testid={`switch-active-${c.id}`}
                        />
                        <span className={`text-[10px] uppercase tracking-widest font-bold ${c.active === false ? "text-red-600" : "text-green-700"}`}>
                          {c.active === false ? "OFF" : "ON"}
                        </span>
                        <button
                          onClick={()=>deleteCompany(c.id, c.name)}
                          data-testid={`delete-company-${c.id}`}
                          title="Elimina azienda"
                          className="ml-1 inline-flex items-center justify-center w-7 h-7 rounded-full text-red-500 hover:bg-red-50 hover:text-red-700 transition-colors"
                        >
                          <Trash2 className="w-3.5 h-3.5"/>
                        </button>
                      </div>
                    </td>
                    <td className="p-4 text-right">
                      <button onClick={(e)=>{e.stopPropagation(); openCompanyDetail(c.id);}} className="text-[#7C3AED] hover:text-[#3B82F6] text-xs font-bold inline-flex items-center gap-1" data-testid={`company-detail-${c.id}`}>
                        Dettaglio <ChevronRight className="w-3 h-3"/>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "companies" && companyDetail && (
        <CompanyDetailView data={companyDetail} loading={loadingDetail} onBack={closeCompanyDetail} onPlanChange={(plan)=>{
          // refresh dopo cambio piano
          openCompanyDetail(companyDetail.company.id);
        }}/>
      )}

      {tab === "users" && (
        <div className="card-smart overflow-hidden" data-testid="sa-users">
          <table className="w-full text-sm">
            <thead className="border-b border-[#1F2540]"><tr className="text-left"><th className="p-4">Nome</th><th className="p-4">Email</th><th className="p-4">Ruolo</th><th className="p-4">Piano</th><th className="p-4">Azioni</th></tr></thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-[#1F2540]">
                  <td className="p-4 font-semibold">{u.full_name}</td>
                  <td className="p-4">{u.email}</td>
                  <td className="p-4 text-xs">{u.role === "superadmin" ? <span className="bg-[#0A0A0A] text-[#3B82F6] px-2 py-1 rounded-full font-semibold">SUPER</span> : u.role}</td>
                  <td className="p-4">
                    <Select value={u.plan || "trial"} onValueChange={(v)=>setPlan(u.id, v)}>
                      <SelectTrigger className="rounded-full h-8 text-xs w-32"><SelectValue/></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trial">Trial</SelectItem>
                        <SelectItem value="starter">Starter</SelectItem>
                        <SelectItem value="business">Business</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </td>
                  <td className="p-4">
                    {u.role !== "superadmin" && (
                      <Button size="icon" variant="ghost" onClick={()=>delUser(u.id)} className="hover:bg-red-50 hover:text-red-600 rounded-full h-8 w-8"><Trash2 className="w-3.5 h-3.5"/></Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "leads" && (
        <div className="card-smart overflow-hidden" data-testid="sa-leads">
          <table className="w-full text-sm">
            <thead className="border-b border-[#1F2540]"><tr className="text-left"><th className="p-4">Data</th><th className="p-4">Nome</th><th className="p-4">Contatti</th><th className="p-4">Azienda</th><th className="p-4">Dim.</th><th className="p-4">Stato</th></tr></thead>
            <tbody>
              {leads.map((l) => (
                <tr key={l.id} className="border-b border-[#1F2540]">
                  <td className="p-4 text-xs text-[#757575]">{new Date(l.created_at).toLocaleString("it-IT")}</td>
                  <td className="p-4 font-semibold">{l.full_name}</td>
                  <td className="p-4 text-xs"><a href={`mailto:${l.email}`} className="text-[#3B82F6]">{l.email}</a><br/><a href={`tel:${l.phone}`} className="text-[#757575]">{l.phone}</a></td>
                  <td className="p-4">{l.company_name || "—"}</td>
                  <td className="p-4 text-xs">{l.company_size}</td>
                  <td className="p-4"><span className="text-xs bg-[#3B82F6]/15 text-[#7C3AED] px-2 py-1 rounded-full">{l.status}</span></td>
                </tr>
              ))}
              {leads.length === 0 && <tr><td colSpan="6" className="p-8 text-center text-[#757575]">Nessun lead ricevuto.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "quotes" && (
        <div className="space-y-3" data-testid="sa-quotes">
          <div className="card-smart p-5 flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-[#1E2347] flex items-center justify-center flex-shrink-0"><Briefcase className="w-6 h-6 text-[#3B82F6]"/></div>
            <div>
              <h2 className="font-display text-2xl">Preventivi Enterprise</h2>
              <p className="text-sm text-[#757575] mt-1">Aziende con più di 50 dipendenti che hanno richiesto un preventivo personalizzato.</p>
            </div>
          </div>

          {quotes.length === 0 ? (
            <div className="card-smart p-10 text-center text-[#757575] text-sm">Nessuna richiesta preventivo ricevuta.</div>
          ) : (
            quotes.map((q) => (
              <div key={q.id} className="card-smart p-5 md:p-6" data-testid={`quote-${q.id}`}>
                <div className="flex flex-col md:flex-row md:items-start gap-5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-2">
                      <span className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${
                        q.status === "won" ? "bg-green-100 text-green-700" :
                        q.status === "contacted" ? "bg-amber-100 text-amber-700" :
                        q.status === "lost" ? "bg-gray-200 text-gray-600" :
                        "bg-[#3B82F6] text-white"
                      }`}>{q.status === "new" ? "Nuovo" : q.status}</span>
                      <span className="text-xs text-[#757575]">{new Date(q.created_at).toLocaleString("it-IT")}</span>
                      {q.multi_site && <span className="text-[10px] bg-[#0A0A0A] text-white px-2 py-0.5 rounded-full">Multi-sede</span>}
                    </div>
                    <h3 className="font-display text-2xl text-[#0A0A0A] leading-tight mb-1">{q.company_name}</h3>
                    <div className="flex flex-wrap gap-3 text-sm text-[#555] mb-3">
                      <span className="inline-flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-[#3B82F6]"/> <strong className="text-[#0A0A0A]">{q.employees_count}</strong> dipendenti</span>
                      {q.sectors && <span className="inline-flex items-center gap-1.5">🏷️ {q.sectors}</span>}
                    </div>
                    <div className="bg-[#0A0E27] rounded-xl p-3 mb-3 text-sm">
                      <div className="font-semibold text-[#0A0A0A]">{q.full_name} {q.role && <span className="text-[#757575] font-normal">· {q.role}</span>}</div>
                      <div className="flex flex-wrap gap-3 mt-1 text-xs">
                        <a href={`mailto:${q.email}`} className="text-[#7C3AED] hover:underline inline-flex items-center gap-1"><Mail className="w-3 h-3"/> {q.email}</a>
                        <a href={`tel:${q.phone}`} className="text-[#0A0A0A] hover:underline inline-flex items-center gap-1"><Phone className="w-3 h-3"/> {q.phone}</a>
                      </div>
                    </div>
                    {q.needed_features && q.needed_features.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {q.needed_features.map((f) => (
                          <span key={f} className="text-[10px] uppercase tracking-widest bg-[#1E2347] text-[#7C3AED] px-2 py-1 rounded-full font-bold">{f}</span>
                        ))}
                      </div>
                    )}
                    {q.message && (
                      <div className="bg-white border-l-2 border-[#3B82F6] rounded-r-xl px-3 py-2 text-xs text-[#444]">"{q.message}"</div>
                    )}
                  </div>
                  <div className="md:w-48 flex md:flex-col gap-2 flex-shrink-0">
                    <select
                      value={q.status}
                      onChange={async (e) => {
                        try { await api.patch(`/admin/enterprise-quotes/${q.id}/status?status=${e.target.value}`); api.get("/admin/enterprise-quotes").then((r) => setQuotes(r.data)); toast.success("Stato aggiornato"); }
                        catch { toast.error("Errore"); }
                      }}
                      data-testid={`quote-status-${q.id}`}
                      className="rounded-full bg-white border border-[#1F2540] text-xs px-3 py-2 w-full"
                    >
                      <option value="new">Nuovo</option>
                      <option value="contacted">Contattato</option>
                      <option value="won">Acquisito 🎉</option>
                      <option value="lost">Perso</option>
                    </select>
                    <a href={`tel:${q.phone}`} className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED] text-white text-xs font-semibold px-3 py-2 text-center">📞 Chiama</a>
                    <a href={`mailto:${q.email}?subject=Preventivo Verixa per ${q.company_name}`} className="rounded-full bg-[#0A0A0A] hover:bg-[#1a1a1a] text-white text-xs font-semibold px-3 py-2 text-center">📧 Email</a>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {tab === "payments" && (
        <div className="card-smart overflow-hidden" data-testid="sa-payments">
          <table className="w-full text-sm">
            <thead className="border-b border-[#1F2540]"><tr className="text-left"><th className="p-4">Data</th><th className="p-4">Piano</th><th className="p-4">Importo</th><th className="p-4">Stato</th><th className="p-4">Session ID</th></tr></thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-[#1F2540]">
                  <td className="p-4 text-xs">{new Date(p.created_at).toLocaleString("it-IT")}</td>
                  <td className="p-4 font-semibold">{p.plan_id}</td>
                  <td className="p-4 font-display text-base">€{p.amount?.toFixed(2)}</td>
                  <td className="p-4"><span className={`text-xs px-2 py-1 rounded-full font-semibold uppercase ${p.payment_status === "paid" ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"}`}>{p.payment_status}</span></td>
                  <td className="p-4 text-[10px] font-mono text-[#757575]">{p.session_id?.slice(0, 20)}…</td>
                </tr>
              ))}
              {payments.length === 0 && <tr><td colSpan="5" className="p-8 text-center text-[#757575]">Nessuna transazione.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "anomalies" && (
        <div data-testid="sa-anomalies">
          <div className="card-smart p-4 mb-4 flex items-end gap-3 flex-wrap">
            <div>
              <Label className="text-xs">Stato</Label>
              <Select value={anomalyFilter.status} onValueChange={(v) => setAnomalyFilter({ ...anomalyFilter, status: v })}>
                <SelectTrigger className="rounded-xl mt-1 w-40" data-testid="anom-filter-status"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">In attesa</SelectItem>
                  <SelectItem value="approved">Approvate</SelectItem>
                  <SelectItem value="rejected">Rifiutate</SelectItem>
                  <SelectItem value=" ">Tutte</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Dal</Label>
              <Input type="date" value={anomalyFilter.from_date} onChange={(e) => setAnomalyFilter({ ...anomalyFilter, from_date: e.target.value })} data-testid="anom-from" className="rounded-xl mt-1"/>
            </div>
            <div>
              <Label className="text-xs">Al</Label>
              <Input type="date" value={anomalyFilter.to_date} onChange={(e) => setAnomalyFilter({ ...anomalyFilter, to_date: e.target.value })} data-testid="anom-to" className="rounded-xl mt-1"/>
            </div>
            <Button onClick={loadAnomalies} data-testid="anom-apply" className="rounded-full bg-[#3B82F6] hover:bg-[#7C3AED]">Filtra</Button>
            <Button onClick={exportAnomalies} variant="outline" data-testid="anom-export" className="rounded-full"><Download className="w-4 h-4 mr-1"/>Esporta CSV</Button>
            <Button onClick={sendWeeklyReportNow} variant="outline" data-testid="weekly-report-now" className="rounded-full text-[#3B82F6] border-[#3B82F6]/40 hover:bg-[#1E2347]">
              <Mail className="w-4 h-4 mr-1"/>Invia report settimanale ora
            </Button>
            <div className="ml-auto text-sm text-[#757575]">{anomalies.length} anomalie</div>
          </div>
          <div className="card-smart overflow-hidden">
            <table className="w-full text-sm">
              <thead className="border-b border-[#1F2540] bg-[#0A0E27]">
                <tr className="text-left">
                  <th className="p-3">Data e ora</th>
                  <th className="p-3">Azienda</th>
                  <th className="p-3">Dipendente</th>
                  <th className="p-3">Sede QR</th>
                  <th className="p-3 text-right">Distanza</th>
                  <th className="p-3">Stato</th>
                  <th className="p-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {anomalies.map((a) => {
                  const status = a.anomaly_status || "pending";
                  const badgeClass = status === "approved" ? "bg-green-100 text-green-700" : status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700";
                  return (
                    <tr key={a.id} className="border-b border-[#1F2540] hover:bg-[#0A0E27]/50">
                      <td className="p-3 text-xs">{new Date(a.timestamp).toLocaleString("it-IT")}</td>
                      <td className="p-3 font-semibold">{a.company_name}</td>
                      <td className="p-3">{a.user_name} <span className="text-[#757575] text-xs">· {a.kind === "in" ? "Entrata" : "Uscita"}</span></td>
                      <td className="p-3 text-xs">{a.qr_label}</td>
                      <td className="p-3 text-right font-mono text-red-600 font-bold">{a.distance_m}m</td>
                      <td className="p-3"><span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase ${badgeClass}`}>{status === "pending" ? "In attesa" : status === "approved" ? "Approvata" : "Rifiutata"}</span></td>
                      <td className="p-3 text-right space-x-1 whitespace-nowrap">
                        <a href={`https://www.openstreetmap.org/?mlat=${a.latitude}&mlon=${a.longitude}#map=17/${a.latitude}/${a.longitude}`} target="_blank" rel="noreferrer" className="inline-flex items-center px-2 py-1 rounded-lg hover:bg-[#1E2347] text-[#3B82F6] text-xs" title="Vedi posizione"><MapPin className="w-3.5 h-3.5"/></a>
                        {status === "pending" && (
                          <>
                            <button onClick={() => decideAnomaly(a.id, "approved")} data-testid={`anom-approve-${a.id}`} className="inline-flex items-center px-2 py-1 rounded-lg hover:bg-green-50 text-green-700 text-xs" title="Approva"><Check className="w-3.5 h-3.5"/></button>
                            <button onClick={() => decideAnomaly(a.id, "rejected")} data-testid={`anom-reject-${a.id}`} className="inline-flex items-center px-2 py-1 rounded-lg hover:bg-red-50 text-red-700 text-xs" title="Rifiuta"><X className="w-3.5 h-3.5"/></button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {anomalies.length === 0 && <tr><td colSpan="7" className="p-12 text-center text-[#757575]"><AlertTriangle className="w-8 h-8 mx-auto mb-2 opacity-30"/>Nessuna anomalia trovata.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// =================== Sotto-componente: Dashboard dettaglio Azienda ===================
function CompanyDetailView({ data, loading, onBack, onPlanChange, onToggleActive }) {
  if (loading) return <div className="p-12 text-center text-[#757575]">Caricamento dashboard azienda…</div>;
  if (!data) return null;
  const { company, admin_user, employees, counters, plan_info, invoices, recent_activity } = data;
  const isActive = company.active !== false;

  const updatePlan = async (newPlan) => {
    try {
      if (admin_user?.id) {
        // Caso classico: aggiorna tramite l'admin utente della company
        await api.patch(`/admin/users/${admin_user.id}/plan?plan=${newPlan}`);
      } else {
        // Fallback: company senza admin user → endpoint diretto per company
        await api.patch(`/admin/companies/${company.id}/plan?plan=${newPlan}`);
      }
      toast.success(`Piano aggiornato a ${newPlan}`);
      onPlanChange(newPlan);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore aggiornamento piano"); }
  };

  const fmtDate = (s) => s ? new Date(s).toLocaleDateString("it-IT") : "—";
  const fmtMoney = (v) => v != null ? `€${Number(v).toFixed(2)}` : "—";

  const kpiGroups = [
    {
      title: "HR · Presenze",
      cards: [
        { icon: QrCode, label: "QR code attivi", value: counters.qr_codes },
        { icon: Activity, label: "Timbrature totali", value: counters.attendances_total },
        { icon: Clock, label: "Timbrature questo mese", value: counters.attendances_month },
        { icon: AlertTriangle, label: "Anomalie pending", value: counters.anomalies_pending, danger: counters.anomalies_pending > 0 },
      ],
    },
    {
      title: "Comunicazioni & Documenti",
      cards: [
        { icon: FileText, label: "Documenti totali", value: counters.documents_total },
        { icon: FileSignature, label: "Firme richieste", value: counters.signature_requests_total },
        { icon: Check, label: "Firme completate", value: counters.signature_requests_signed, accent: "green" },
        { icon: Clock, label: "Firme pending", value: counters.signature_requests_pending, danger: counters.signature_requests_pending > 0 },
      ],
    },
    {
      title: "Richieste & Servizi",
      cards: [
        { icon: CalendarRange, label: "Ferie/Permessi", value: counters.leaves_total },
        { icon: Clock, label: "Ferie pending", value: counters.leaves_pending, danger: counters.leaves_pending > 0 },
        { icon: Wallet, label: "Rimborsi", value: counters.expenses_total },
        { icon: Clock, label: "Rimborsi pending", value: counters.expenses_pending, danger: counters.expenses_pending > 0 },
        { icon: MapPin, label: "Trasferte", value: counters.trasferte_total },
        { icon: Receipt, label: "Cedolini emessi", value: counters.payslips_total },
        { icon: Clock, label: "Turni", value: counters.shifts_total },
        { icon: Activity, label: "Rapportini (timesheet)", value: counters.timesheets_total },
      ],
    },
  ];

  const PLAN_OPTIONS = ["trial", "tier10", "tier20", "tier30", "tier50"];

  return (
    <div data-testid="company-detail-view" className="space-y-5">
      <div className="flex items-center gap-3 flex-wrap">
        <button onClick={onBack} data-testid="company-detail-back" className="rounded-full bg-white border border-[#1F2540] hover:border-[#3B82F6] px-4 py-2 text-sm font-semibold flex items-center gap-1.5">
          <ArrowLeft className="w-4 h-4"/> Torna alle aziende
        </button>
        <span className="text-xs text-[#757575]">/</span>
        <span className="text-xs text-[#757575]">{company.name}</span>
        <div className="ml-auto flex items-center gap-2 bg-white border border-[#1F2540] rounded-full px-3 py-1.5" data-testid="company-detail-toggle-active">
          <Power className={`w-3.5 h-3.5 ${isActive ? "text-green-600" : "text-red-600"}`}/>
          <span className={`text-[10px] uppercase tracking-widest font-bold ${isActive ? "text-green-700" : "text-red-700"}`}>
            {isActive ? "ATTIVA" : "DISATTIVATA"}
          </span>
          <Switch
            checked={isActive}
            onCheckedChange={()=>onToggleActive && onToggleActive(company.id, isActive)}
            data-testid="company-detail-active-switch"
          />
        </div>
      </div>

      {!isActive && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-4 flex items-start gap-3" data-testid="company-deactivated-banner">
          <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5"/>
          <div className="text-sm text-red-800">
            <strong>Azienda disattivata.</strong> Gli utenti di questa azienda non possono più accedere al sistema.
            {company.deactivated_at && <span className="block text-xs text-red-600/80 mt-0.5">Disattivata il {new Date(company.deactivated_at).toLocaleString("it-IT")}{company.deactivation_reason ? ` · Motivo: "${company.deactivation_reason}"` : ""}.</span>}
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 card-smart p-6">
          <div className="flex items-start gap-4 mb-4">
            <div className="w-14 h-14 rounded-2xl bg-[#1E2347] flex items-center justify-center flex-shrink-0">
              <Building2 className="w-7 h-7 text-[#3B82F6]"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] uppercase tracking-widest text-[#757575] mb-0.5">Anagrafica azienda</div>
              <h2 className="font-display text-2xl md:text-3xl leading-tight">{company.name}</h2>
              <div className="text-sm text-[#555] mt-0.5">{company.legal_form || "—"} · creata il {fmtDate(company.created_at)}</div>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <Field label="P.IVA" value={company.vat_number}/>
            <Field label="Codice Fiscale" value={company.fiscal_code}/>
            <Field label="PEC" value={company.pec}/>
            <Field label="SDI" value={company.sdi_code}/>
            <Field label="IBAN" value={company.iban} mono/>
            <Field label="REA" value={company.rea_code || company.rea}/>
            <Field label="Email" value={company.email}/>
            <Field label="Telefono" value={company.phone}/>
            <Field label="Indirizzo legale" value={`${company.address || ""}${company.zip_code ? ", " + company.zip_code : ""}${company.city ? " " + company.city : ""}${company.province ? " (" + company.province + ")" : ""}`.trim() || null} full/>
            <Field label="Referente" value={typeof company.contact_person === "object" && company.contact_person ? `${company.contact_person.first_name || ""} ${company.contact_person.last_name || ""}`.trim() || (company.contact_person.role || null) : company.contact_person}/>
            <Field label="Email referente" value={typeof company.contact_person === "object" && company.contact_person ? (company.contact_person.email || null) : company.contact_email}/>
          </div>
          {admin_user && (
            <div className="mt-4 pt-4 border-t border-[#1F2540] flex flex-wrap items-center gap-3 text-sm">
              <span className="text-xs text-[#757575] uppercase tracking-widest">Titolare account:</span>
              <span className="font-semibold text-[#0A0A0A]">{admin_user.full_name || admin_user.email}</span>
              <a href={`mailto:${admin_user.email}`} className="text-[#7C3AED] hover:underline text-xs">{admin_user.email}</a>
            </div>
          )}
        </div>

        <div className="card-smart p-6 bg-gradient-to-br from-[#0A0A0A] to-[#1a1a1a] text-white" data-testid="company-detail-plan">
          <div className="text-[10px] uppercase tracking-widest text-[#3B82F6] font-bold mb-2">Piano attivo</div>
          <div className="font-display text-3xl mb-1">{plan_info.plan_name}</div>
          <div className="text-sm text-white/60 mb-4">{plan_info.plan_interval === "yearly" ? "Annuale" : "Mensile"}</div>

          <div className="space-y-2 text-xs mb-4">
            {plan_info.plan_price != null && (
              <div className="flex justify-between border-b border-white/10 pb-1.5"><span className="text-white/60">Canone</span><strong>{fmtMoney(plan_info.plan_price)}</strong></div>
            )}
            {plan_info.plan_setup_fee != null && (
              <div className="flex justify-between border-b border-white/10 pb-1.5"><span className="text-white/60">Setup</span><strong>{fmtMoney(plan_info.plan_setup_fee)}</strong></div>
            )}
            {plan_info.plan_per_employee_fee != null && (
              <div className="flex justify-between border-b border-white/10 pb-1.5"><span className="text-white/60">Per dipendente</span><strong>{fmtMoney(plan_info.plan_per_employee_fee)}/m</strong></div>
            )}
            <div className="flex justify-between border-b border-white/10 pb-1.5"><span className="text-white/60">Posti dipendenti</span><strong>{counters.employees_active}/{plan_info.plan_seats}</strong></div>
            <div className="flex justify-between"><span className="text-white/60">Scadenza</span><strong>{fmtDate(plan_info.plan_expires_at)}</strong></div>
          </div>

          <div className="flex flex-wrap gap-1.5 mb-3">
            {PLAN_OPTIONS.map((p) => (
              <button
                key={p}
                onClick={()=>updatePlan(p)}
                data-testid={`plan-set-${p}`}
                className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1 rounded-full border transition-all ${plan_info.plan === p ? "bg-[#3B82F6] text-white border-[#3B82F6]" : "bg-transparent text-white/70 border-white/20 hover:border-[#3B82F6]"}`}
              >
                {p}
              </button>
            ))}
          </div>
          <div className="text-[10px] text-white/40">Click per cambiare piano (apply su admin user)</div>
        </div>
      </div>

      {kpiGroups.map((g) => (
        <div key={g.title}>
          <div className="text-[10px] uppercase tracking-widest text-[#757575] font-bold mb-2 px-1">{g.title}</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {g.cards.map((card, i) => (
              <KPI key={i} {...card}/>
            ))}
          </div>
        </div>
      ))}

      <div>
        <div className="flex items-end justify-between mb-2 px-1">
          <div className="text-[10px] uppercase tracking-widest text-[#757575] font-bold">Dipendenti ({employees.length})</div>
        </div>
        <div className="card-smart overflow-hidden" data-testid="company-detail-employees">
          <table className="w-full text-sm">
            <thead className="border-b border-[#1F2540] bg-[#0A0E27]">
              <tr className="text-left">
                <th className="p-3">Nome</th>
                <th className="p-3">Email</th>
                <th className="p-3">Ruolo</th>
                <th className="p-3 text-center">Timbrature</th>
                <th className="p-3 text-center">Ferie</th>
                <th className="p-3 text-center">Rimborsi</th>
                <th className="p-3 text-center">Rapportini</th>
                <th className="p-3 text-right">Stato</th>
              </tr>
            </thead>
            <tbody>
              {employees.length === 0 ? (
                <tr><td colSpan="8" className="p-10 text-center text-[#757575]">Nessun dipendente registrato per questa azienda.</td></tr>
              ) : employees.map((e) => (
                <tr key={e.id} className="border-b border-[#1F2540] hover:bg-[#0A0E27]" data-testid={`emp-row-${e.id}`}>
                  <td className="p-3 font-semibold">{e.name} {e.lastname}</td>
                  <td className="p-3 text-xs">{e.email || "—"}</td>
                  <td className="p-3 text-xs text-[#757575]">{e.job || "—"}</td>
                  <td className="p-3 text-center font-semibold">{e.stats.attendances}</td>
                  <td className="p-3 text-center">{e.stats.leaves}</td>
                  <td className="p-3 text-center">{e.stats.expenses}</td>
                  <td className="p-3 text-center">{e.stats.timesheets}</td>
                  <td className="p-3 text-right">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${e.enabled ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"}`}>
                      {e.enabled ? "Attivo" : "Disattivo"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-widest text-[#757575] font-bold mb-2 px-1">Fatture & Pagamenti ({invoices.length})</div>
        <div className="card-smart overflow-hidden" data-testid="company-detail-invoices">
          <table className="w-full text-sm">
            <thead className="border-b border-[#1F2540] bg-[#0A0E27]">
              <tr className="text-left">
                <th className="p-3">Data</th>
                <th className="p-3">N. Fattura</th>
                <th className="p-3">Piano</th>
                <th className="p-3 text-right">Importo</th>
                <th className="p-3 text-right">Stato</th>
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr><td colSpan="5" className="p-8 text-center text-[#757575]">Nessuna fattura emessa.</td></tr>
              ) : invoices.map((inv) => (
                <tr key={inv.id} className="border-b border-[#1F2540]" data-testid={`inv-row-${inv.id}`}>
                  <td className="p-3 text-xs text-[#757575]">{fmtDate(inv.created_at)}</td>
                  <td className="p-3 font-mono text-xs">{inv.invoice_number || inv.session_id?.slice(-12) || "—"}</td>
                  <td className="p-3 text-xs">{inv.plan_id || "—"}</td>
                  <td className="p-3 text-right font-display text-base">{fmtMoney(inv.amount)}</td>
                  <td className="p-3 text-right">
                    <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${inv.payment_status === "paid" ? "bg-green-100 text-green-700" : inv.payment_status === "pending" ? "bg-amber-100 text-amber-700" : "bg-gray-200 text-gray-600"}`}>
                      {inv.payment_status || inv.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {recent_activity.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#757575] font-bold mb-2 px-1">Attività recente · azienda ↔ dipendenti</div>
          <div className="card-smart p-4 space-y-2" data-testid="company-detail-activity">
            {recent_activity.map((a, i) => {
              const Icon = a.kind === "attendance" ? QrCode : a.kind === "leave" ? CalendarRange : FileSignature;
              return (
                <div key={i} className="flex items-center gap-3 text-sm py-1.5 border-b last:border-b-0 border-[#1F2540]">
                  <Icon className="w-4 h-4 text-[#3B82F6] flex-shrink-0"/>
                  <span className="text-[#0A0A0A] font-semibold flex-shrink-0 w-32 truncate">{a.employee || "—"}</span>
                  <span className="text-[#555] flex-1 truncate">{a.label}</span>
                  <span className="text-xs text-[#999] flex-shrink-0">{a.ts ? new Date(a.ts).toLocaleString("it-IT", {day:"2-digit", month:"2-digit", hour:"2-digit", minute:"2-digit"}) : "—"}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, mono, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-widest text-[#999]">{label}</div>
      <div className={`text-sm text-[#0A0A0A] ${mono ? "font-mono" : ""} ${value ? "" : "text-[#999]"}`}>{value || "—"}</div>
    </div>
  );
}

function KPI({ icon: Icon, label, value, accent, danger }) {
  return (
    <div className={`card-smart p-4 ${danger ? "border-amber-300 bg-amber-50" : ""}`}>
      <div className="flex items-center gap-2 mb-1.5">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${accent === "green" ? "bg-green-100 text-green-600" : danger ? "bg-amber-200 text-amber-700" : "bg-[#1E2347] text-[#3B82F6]"}`}>
          <Icon className="w-4 h-4"/>
        </div>
      </div>
      <div className="font-display text-3xl tracking-tight text-[#0A0A0A]">{value ?? 0}</div>
      <div className="text-[11px] text-[#757575] mt-0.5 leading-tight">{label}</div>
    </div>
  );
}
